import MalinaOffice from "@/assets/MalinaOffice.png";
import MalinaOutdoor from "@/assets/MalinaOutdoor.png";
import MalinaJacke from "@/assets/MalinaJacke.png";
import Malina from "@/assets/Malina.png";
import Lars from "@/assets/Lars.png";
import Anna from "@/assets/Anna.png";
import Tyler from "@/assets/Tyler.png";
import David from "@/assets/David.png";

import { EBrandName } from "@/types/TInfoBox";
import type { AvatarQuality, VoiceEmotion } from "@/types/TAvatarSettings";

export const DEFAULT_SYSTEM_MESSAGE = `You are a helpful assistant specializing in answering questions based on the available knowledge base.

IMPORTANT GUIDELINES:
- Always maintain a professional, friendly, and helpful tone.
- If a user asks about topics outside your area of expertise, politely let them know and redirect them.

When the user asks for information:
1. Use the 'search' tool to check the knowledge base before answering.
2. Use the 'report_grounding' tool to report the source of information.
3. Provide accurate, clear, and concise answers based on the available documentation.

User Management:
- After authenticating the user, call the 'get_user' tool to fetch user information.
- After updating user info by using 'update_user' tool, call the 'get_user' tool again to fetch updated changes.`;
export const DEFAULT_WELCOME_MESSAGE = "Willkommen! Wie kann ich Ihnen helfen?";
export const DEFAULT_AVATAR_LANGUAGE = "de";

export const AVATARS = [
  {
    avatar_id: "12be85c81aa4407881c829344410f362",
    avatar_name: "Malina mit dunkler Jacke",
    preview_image_url: MalinaJacke,
  },
  {
    avatar_id: "4c44036b5efa4245a45480adacbaf878",
    avatar_name: "Malina im Büro",
    preview_image_url: MalinaOffice,
  },
  {
    avatar_id: "0fb2604fcb85435b8823524ce1d6d489",
    avatar_name: "Malina vor Haus",
    preview_image_url: MalinaOutdoor,
  },
  {
    avatar_id: "c419d617d3014740a26994f10f82dd47",
    avatar_name: "David",
    preview_image_url: David,
  },
  {
    avatar_id: "2d528828ce9e4e859da4c82b60ea44b8",
    avatar_name: "Lars im weißen Poloshirt",
    preview_image_url: Lars,
  },
  {
    avatar_id: "d03f9685cecd4a94a338798812ae9f25",
    avatar_name: "Malina im blauen Pulli",
    preview_image_url: Malina,
  },
  {
    avatar_id: "Anna_public_3_20240108",
    avatar_name: "Anna im braunen T-Shirt",
    preview_image_url: Anna,
  },
  {
    avatar_id: "Tyler-incasualsuit-20220721",
    avatar_name: "Tyler im lässigen Anzug",
    preview_image_url: Tyler,
  },
];

export const STT_LANGUAGE_LIST = [
  { label: "languages.bg", value: "bg", key: "bg" },
  { label: "languages.zh", value: "zh", key: "zh" },
  { label: "languages.de", value: "de", key: "de" },
  { label: "languages.da", value: "da", key: "da" },
  { label: "languages.en", value: "en", key: "en" },
  { label: "languages.fi", value: "fi", key: "fi" },
  { label: "languages.fr", value: "fr", key: "fr" },
  { label: "languages.el", value: "el", key: "el" },
  { label: "languages.hi", value: "hi", key: "hi" },
  { label: "languages.id", value: "id", key: "id" },
  { label: "languages.it", value: "it", key: "it" },
  { label: "languages.ja", value: "ja", key: "ja" },
  { label: "languages.ko", value: "ko", key: "ko" },
  { label: "languages.ms", value: "ms", key: "ms" },
  { label: "languages.nl", value: "nl", key: "nl" },
  { label: "languages.no", value: "no", key: "no" },
  { label: "languages.pl", value: "pl", key: "pl" },
  { label: "languages.pt", value: "pt", key: "pt" },
  { label: "languages.ro", value: "ro", key: "ro" },
  { label: "languages.ru", value: "ru", key: "ru" },
  { label: "languages.sv", value: "sv", key: "sv" },
  { label: "languages.sk", value: "sk", key: "sk" },
  { label: "languages.es", value: "es", key: "es" },
  { label: "languages.cs", value: "cs", key: "cs" },
  { label: "languages.tr", value: "tr", key: "tr" },
  { label: "languages.uk", value: "uk", key: "uk" },
  { label: "languages.hu", value: "hu", key: "hu" },
  { label: "languages.vi", value: "vi", key: "vi" },
];

export const EMOTIONS_LIST: { key: VoiceEmotion; label: string; value: VoiceEmotion }[] = [
  { key: "excited", label: "emotions.excited", value: "excited" },
  { key: "broadcaster", label: "emotions.broadcaster", value: "broadcaster" },
  { key: "friendly", label: "emotions.friendly", value: "friendly" },
  { key: "serious", label: "emotions.serious", value: "serious" },
  { key: "soothing", label: "emotions.soothing", value: "soothing" },
];

export const NAV_ITEMS = [
  { label: "Unternehmen", value: "company" },
  { label: "Produkte", value: "products" },
  { label: "Kundenservice", value: "customerService" },
  { label: "Beratersuche", value: "consultantSearch" },
  { label: "Karriere", value: "career" },
];

export const AVATAR_QUALITY_LIST: { key: AvatarQuality; label: string; value: AvatarQuality }[] = [
  { key: "high", label: "avatarQualities.high", value: "high" },
  { key: "medium", label: "avatarQualities.medium", value: "medium" },
  { key: "low", label: "avatarQualities.low", value: "low" },
];

// Phone number regex - matches various formats including international
// Examples: +49 123 456 7890, 0123-456-789, (123) 456-7890, 123.456.7890
// Fixed to exclude trailing punctuation like dots, commas, etc.
export const PHONE_REGEX = /(?:\+\d{1,3}[-.\s]?)?\(?[\d\s\-\.\(\)]{7,19}[^\s\-\.\(\).,;!?](?=\s|$|[.,;!?])/g;

export const DEFAULT_GROUPPED_LINKS_BY_BRAND = [
  {
    brand: EBrandName.domcura,
    items: []
  },
  {
    brand: EBrandName.nordias,
    items: []
  },
  {
    brand: EBrandName.innoas,
    items: []
  },
  {
    brand: EBrandName.nordvers,
    items: []
  },
  {
    brand: EBrandName.default,
    items: []
  }
]

export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes